import { stringToHslColor } from '@/lib/utils';
import { fromLocalISOString } from '@/utils/timezoneUtils';

// Calculate relative luminance to determine if text should be black or white
function getLuminance(hue: number, saturation: number, lightness: number): number {
  // Convert HSL to relative luminance using a simplified formula
  // Lightness is the main factor, but saturation also affects perceived brightness
  // Higher lightness and lower saturation create brighter colors
  return (lightness + (100 - saturation) * 0.25) / 100;
}

// Generate a color based on the event title and start time
export const generateEventColor = (title: string, startTime: Date, theme?: 'light' | 'dark', intensity: number = 1.0) => {
  // Special colors for specific shift types
  const normalizedTitle = (title || '').trim().toUpperCase();
  if (normalizedTitle === 'ΡΕΠΟ' || normalizedTitle === 'REPO') {
    // Fluorescent red for ΡΕΠΟ/REPO shifts
    return {
      backgroundColor: 'hsl(0, 100%, 70%)', // Bright fluorescent red
      textColor: '#000000'
    };
  }
  
  // Check for leave types (αδειες)
  const leaveTypes = ['ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ', 'ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ', 'ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ', 'ΜΗ ΕΡΓΑΣΙΑ'];
  if (leaveTypes.some(leaveType => title.includes(leaveType))) {
    // Fluorescent yellow for all leave types
    return {
      backgroundColor: 'hsl(60, 100%, 75%)', // Bright fluorescent yellow
      textColor: '#000000'
    };
  }
  
  // Generate base HSL color from the title string only (remove time influence)
  // This ensures the same title always gets the same color regardless of time/week
  const baseColor = stringToHslColor(title, 55, 65);
  
  // Extract hue from baseColor (format is "hsl(H, S%, L%)")
  const hueMatch = baseColor.match(/hsl\((\d+),/);
  const hue = hueMatch ? parseInt(hueMatch[1], 10) : 200;
  
  // Consistent saturation and lightness for pastel colors
  let saturation = 85;  // Higher saturation for more vibrant colors
  let lightness = 75;   // Keep lightness high for pastel feel
  
  // Create subtle variations based on title to distinguish similar titles
  const titleHash = title.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 100;
  
  // Adjust the hue slightly based on the title hash for subtle variety
  const adjustedHue = ((hue + titleHash % 20) % 360);
  
  // Pastel and bright color palette mapping based on the reference image colors
  if (adjustedHue >= 0 && adjustedHue < 30) {
    // Reds/Browns - like EKAAH, ADMINISTRATION, PRINCESS ANDRIANA
    saturation = 75 + (titleHash % 20); // Rich reds and browns
    lightness = 50 + (titleHash % 20); // Medium to dark
  } else if (adjustedHue >= 30 && adjustedHue < 60) {
    // Oranges/Golds - like GENERPHARM, LM CCTV  
    saturation = 80 + (titleHash % 15);
    lightness = 55 + (titleHash % 15);
  } else if (adjustedHue >= 60 && adjustedHue < 120) {
    // Greens/Teals - like POAOE COLOSSOS, LM B.ΣΟΦΙΑΣ, QUEST ACS
    saturation = 70 + (titleHash % 20);
    lightness = 45 + (titleHash % 20);
  } else if (adjustedHue >= 120 && adjustedHue < 180) {
    // Cyan/Turquoise - like QUEST VULON, HILTON
    saturation = 75 + (titleHash % 20);
    lightness = 40 + (titleHash % 25);
  } else if (adjustedHue >= 180 && adjustedHue < 240) {
    // Blues - like BLC AMADEUS, LM ΠΥΛΑΙΑ, QUEST ΝΤΙΓΟΣ
    saturation = 80 + (titleHash % 15);
    lightness = 45 + (titleHash % 20);
  } else if (adjustedHue >= 240 && adjustedHue < 300) {
    // Purples - like POAOE ATLANTICA, LM APODHKES, QUEST ΑΠΓΙΑΕΟΣ
    saturation = 75 + (titleHash % 20);
    lightness = 50 + (titleHash % 20);
  } else {
    // Magentas/Pinks - like LM AIRPORT, SKYWALKER
    saturation = 70 + (titleHash % 25);
    lightness = 55 + (titleHash % 15);
  }
  
  // Ensure colors remain rich and vibrant by clamping values
  saturation = Math.min(Math.max(saturation, 60), 100);
  lightness = Math.min(Math.max(lightness, 35), 75);
  
  // Apply light mode fading - reduce saturation and increase lightness by half
  if (theme === 'light') {
    saturation = Math.max(saturation * 0.5, 15); // Reduce saturation by 1/2, minimum 15%
    lightness = Math.min(lightness + (lightness * 0.5), 92); // Increase lightness by 1/2, maximum 92%
  }
  
  // Apply intensity adjustment
  if (intensity !== 1.0) {
    if (intensity > 1.0) {
      // Super vibrant mode - increase saturation and adjust lightness for maximum vibrancy
      const extraIntensity = intensity - 1.0; // 0.0 to 1.0 (for 200%)
      saturation = Math.min(saturation + (extraIntensity * 40), 100); // Boost saturation up to 40% more
      lightness = Math.max(lightness - (extraIntensity * 20), 30); // Darken more for extreme vibrancy
    } else {
      // Fade mode - reduce saturation and increase lightness
      saturation = Math.max(saturation * intensity, 10); // Minimum 10% saturation
      const lightnessAdjustment = (1 - intensity) * 20; // Max 20% lightness increase
      lightness = Math.min(lightness + lightnessAdjustment, 95); // Maximum 95% lightness
    }
  }
  
  // Generate final color
  const backgroundColor = `hsl(${adjustedHue}, ${saturation}%, ${lightness}%)`;
  
  // Determine text color based on background brightness
  // Use black for light backgrounds, white for dark backgrounds
  const luminance = getLuminance(adjustedHue, saturation, lightness);
  const textColor = luminance > 0.6 ? '#000000' : '#FFFFFF';
  
  return { backgroundColor, textColor };
};

// Generate alternating colors for consecutive titles to avoid similar hues
export const generateAlternatingTitleColors = (titles: string[], events: any[], theme?: 'light' | 'dark', intensity: number = 1.0) => {
  const colors: Record<string, { backgroundColor: string; textColor: string }> = {};
  
  if (titles.length === 0) return colors;
  
  // Define distinct hue ranges that are visually different
  const hueRanges = [
    15,   // Red-Orange
    45,   // Orange-Yellow  
    75,   // Yellow-Green
    120,  // Green
    180,  // Cyan
    220,  // Blue
    270,  // Purple
    315,  // Magenta
    0,    // Red
    60,   // Yellow
    150,  // Teal
    240,  // Deep Blue
    300,  // Violet
    330,  // Pink
  ];
  
  titles.forEach((title, index) => {
    // Special colors for specific shift types (keep existing logic)
    const normalizedTitle = (title || '').trim().toUpperCase();
    if (normalizedTitle === 'ΡΕΠΟ' || normalizedTitle === 'REPO') {
      colors[title] = {
        backgroundColor: 'hsl(0, 100%, 70%)', // Bright fluorescent red
        textColor: '#000000'
      };
      return;
    }
    
    // Check for leave types (αδειες)
    const leaveTypes = ['ΚΑΝ ΑΔΕΙΑ ΠΡΟΗΓ ΕΤΟΥΣ', 'ΚΑΝΟΝΙΚΗ ΑΔΕΙΑ', 'ΑΝΑΡΡΩΤΙΚΗ ΑΔΕΙΑ', 'ΜΗ ΕΡΓΑΣΙΑ'];
    if (leaveTypes.some(leaveType => title.includes(leaveType))) {
      colors[title] = {
        backgroundColor: 'hsl(60, 100%, 75%)', // Bright fluorescent yellow
        textColor: '#000000'
      };
      return;
    }
    
    // Use alternating hue ranges for regular titles
    const hue = hueRanges[index % hueRanges.length];
    
    // Generate some variation based on title to make it unique while keeping it in the same hue family
    const titleHash = title.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 100;
    const hueVariation = (titleHash % 15) - 7; // ±7 degrees of variation
    const adjustedHue = (hue + hueVariation + 360) % 360;
    
    // Set base saturation and lightness for rich colors
    let saturation = 75 + (titleHash % 20); // 75-95%
    let lightness = 50 + (titleHash % 20);  // 50-70%
    
    // Apply theme-specific adjustments
    if (theme === 'light') {
      saturation = Math.max(saturation * 0.6, 20); // Softer colors for light theme
      lightness = Math.min(lightness + 25, 85); // Lighter for better contrast
    }
    
    // Apply intensity adjustment
    if (intensity !== 1.0) {
      if (intensity > 1.0) {
        // Super vibrant mode - increase saturation and adjust lightness for maximum vibrancy
        const extraIntensity = intensity - 1.0; // 0.0 to 1.0 (for 200%)
        saturation = Math.min(saturation + (extraIntensity * 40), 100); // Boost saturation up to 40% more
        lightness = Math.max(lightness - (extraIntensity * 20), 30); // Darken more for extreme vibrancy
      } else {
        // Fade mode - reduce saturation and increase lightness
        saturation = Math.max(saturation * intensity, 10); // Minimum 10% saturation
        const lightnessAdjustment = (1 - intensity) * 20; // Max 20% lightness increase
        lightness = Math.min(lightness + lightnessAdjustment, 95); // Maximum 95% lightness
      }
    }
    
    // Ensure colors remain within valid ranges
    saturation = Math.min(Math.max(saturation, 10), 100);
    lightness = Math.min(Math.max(lightness, 20), 90);
    
    // Generate final color
    const backgroundColor = `hsl(${adjustedHue}, ${saturation}%, ${lightness}%)`;
    
    // Determine text color based on background brightness
    const luminance = getLuminance(adjustedHue, saturation, lightness);
    const textColor = luminance > 0.6 ? '#000000' : '#FFFFFF';
    
    colors[title] = { backgroundColor, textColor };
  });
  
  return colors;
};

// Utility function to consistently sort events by title
export const sortEventsByTitle = (events: any[]) => {
  // Get unique titles and sort them alphabetically
  const uniqueTitles = [...new Set(events.map(event => event.title))].sort();
  
  // Create a map of title -> index for sorting
  const titleOrder = uniqueTitles.reduce((acc, title, index) => {
    acc[title] = index;
    return acc;
  }, {} as Record<string, number>);
  
  // Sort events by title order
  return [...events].sort((a, b) => {
    // First sort by title order
    const titleDiff = (titleOrder[a.title] || 999) - (titleOrder[b.title] || 999);
    
    // If titles are the same, sort by start time
    if (titleDiff === 0) {
      return fromLocalISOString(a.start_date).getTime() - fromLocalISOString(b.start_date).getTime();
    }
    
    return titleDiff;
  });
};
